import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { ContractDocument } from '@/services/contractsService'

interface ContractDocumentsProps {
  documents: ContractDocument[]
  loading?: boolean
  onDownload?: (documentId: string) => Promise<void>
}

const ContractDocuments: React.FC<ContractDocumentsProps> = ({
  documents,
  loading = false,
  onDownload,
}) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const handleDownload = async (document: ContractDocument) => {
    if (!onDownload || downloadingId) return

    setDownloadingId(document._id)
    try {
      await onDownload(document._id)
    } catch (error) {
      console.error('Failed to download document:', error)
    } finally {
      setDownloadingId(null)
    }
  }

  // Extract document name from fileKey or title
  const getDocumentName = (document: ContractDocument) => {
    if (document.fileKey) {
      const parts = document.fileKey.split('/')
      return parts[parts.length - 1] || document.title
    }
    return document.title
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Contract Documents
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : documents.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No contract documents available
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Document Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Created On
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    Download
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map(document => (
                  <TableRow key={document._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DescriptionIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {document.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(document.createdAt), 'MM/dd/yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(document)}
                        disabled={downloadingId === document._id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {downloadingId === document._id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default ContractDocuments
